import { getAllFields } from "../helper/collection.helper";
import { TryCatchAction } from "../helper/redux.helpers";
import { store } from "../store";
import {
  RECOMMENDATION_CONFIG_ACTION,
  SINGLE_RECOMMENDATION_ACTION,
} from "../store/collection/collection.action";
import {
  selectRecommendationConfig,
  selectSelectedCollection,
} from "../store/collection/collection.selector";
import {
  IRecommendationConfig,
  ISingleProductRecommendation,
  ISingleProductRecommendationResponse,
  VectorOperation,
} from "../types";
import { ApiError } from "./network.service";
import VectorAIBase from "./vectorai-base.service";

export type Metric = "cosine" | "l1" | "l2" | "dp";

interface IRecommendationBaseQuery {
  collection_name?: string;
  search_field: string;
  approx: number;
  sum_fields: boolean;
  page_size: number;
  page: number;
  metric: Metric;
  min_score: number;
  include_vector: boolean;
  include_count: boolean;
  hundred_scale: boolean;
}

interface ISingleProductRecommendationQuery extends IRecommendationBaseQuery {
  document_id: string;
}

interface IMultiProductRecommendationQuery extends IRecommendationBaseQuery {
  document_ids: string[];
  vector_operation: VectorOperation;
}

interface IMultiProductRecommendationLikeDislike
  extends IRecommendationBaseQuery {
  positive_document_ids: string[];
  negative_document_ids: string[];
  vector_operation: VectorOperation;
}

class RecommendationService extends VectorAIBase {
  @TryCatchAction(SINGLE_RECOMMENDATION_ACTION)
  public async getSingleProductRecommendationById(
    query: ISingleProductRecommendationQuery
  ): Promise<ISingleProductRecommendation> {
    const collection = selectSelectedCollection(store.getState());
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    query.collection_name = collection.data?.name;
    const response:
      | ISingleProductRecommendationResponse
      | undefined = await this.get("/collection/search_by_id", query);

    if (!response) {
      throw new ApiError("Something went wrong...");
    }
    return {
      currentPage: query.page,
      pageSize: query.page_size,
      data: response.results,
      total: response.count,
      fields: getAllFields(collection.data?.schema || {}),
    };
  }

  @TryCatchAction(SINGLE_RECOMMENDATION_ACTION)
  public async getMultiProductRecommendationById(
    query: IMultiProductRecommendationQuery
  ): Promise<ISingleProductRecommendation> {
    const collection = selectSelectedCollection(store.getState());
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    query.collection_name = collection.data?.name;
    const response:
      | ISingleProductRecommendationResponse
      | undefined = await this.get("/collection/search_by_ids", query);

    if (!response) {
      throw new ApiError("Something went wrong...");
    }
    return {
      currentPage: query.page,
      pageSize: query.page_size,
      data: response.results,
      total: response.count,
      fields: getAllFields(collection.data?.schema || {}),
    };
  }

  public updateRecommendationConfig(config: Partial<IRecommendationConfig>) {
    const recommendationConfig = selectRecommendationConfig(store.getState());
    const updatedConfig: IRecommendationConfig = {
      ...recommendationConfig,
      ...config,
    };
    if (
      (config.document_id && updatedConfig.setup) ||
      (config.document_ids?.length && updatedConfig.setup) ||
      (config.positive_document_ids?.length &&
        config.negative_document_ids?.length &&
        updatedConfig.setup)
    ) {
      updatedConfig.setup = false;
    }
    if (
      config.type?.value === "MULIT_PRODUCT_RECOMMENDATION" &&
      !updatedConfig.document_ids.length
    ) {
      updatedConfig.setup = true;
      updatedConfig.document_ids = [];
    }

    if (
      config.type?.value === "SINGLE_PRODUCT_RECOMMENDATION" &&
      !updatedConfig.document_id
    ) {
      updatedConfig.setup = true;
      updatedConfig.document_id = "";
    }
    if (
      config.type?.value === "MULTI_PRODUCT_LIKE_DISLIKE_RECOMMEMDATION" &&
      !(
        updatedConfig.positive_document_ids.length &&
        updatedConfig.negative_document_ids.length
      )
    ) {
      updatedConfig.setup = true;
      updatedConfig.positive_document_ids = [];
      updatedConfig.negative_document_ids = [];
    }
    if (
      updatedConfig.search_field &&
      updatedConfig.document_id &&
      updatedConfig.type.value === "SINGLE_PRODUCT_RECOMMENDATION"
    ) {
      this.getSingleProductRecommendationById({
        document_id: updatedConfig.document_id,
        search_field: updatedConfig.search_field,
        approx: updatedConfig.approx,
        sum_fields: updatedConfig.sum_fields,
        page_size: updatedConfig.page_size,
        page: updatedConfig.page,
        metric: updatedConfig.metric,
        min_score: updatedConfig.min_score,
        include_vector: updatedConfig.include_vector,
        include_count: updatedConfig.include_count,
        hundred_scale: updatedConfig.hundred_scale,
      });
    }
    if (
      updatedConfig.document_ids.length &&
      updatedConfig.type.value === "MULIT_PRODUCT_RECOMMENDATION"
    ) {
      this.getMultiProductRecommendationById({
        document_ids: updatedConfig.document_ids,
        vector_operation: updatedConfig.vector_operation.value,
        search_field: updatedConfig.search_field,
        approx: updatedConfig.approx,
        sum_fields: updatedConfig.sum_fields,
        page_size: updatedConfig.page_size,
        page: updatedConfig.page,
        metric: updatedConfig.metric,
        min_score: updatedConfig.min_score,
        include_vector: updatedConfig.include_vector,
        include_count: updatedConfig.include_count,
        hundred_scale: updatedConfig.hundred_scale,
      });
    }

    if (
      updatedConfig.positive_document_ids.length &&
      updatedConfig.negative_document_ids.length &&
      updatedConfig.type.value === "MULTI_PRODUCT_LIKE_DISLIKE_RECOMMEMDATION"
    ) {
      this.getMultiProductRecomendationLikeDislike({
        positive_document_ids: updatedConfig.positive_document_ids,
        negative_document_ids: updatedConfig.negative_document_ids,
        vector_operation: updatedConfig.vector_operation.value,
        search_field: updatedConfig.search_field,
        approx: updatedConfig.approx,
        sum_fields: updatedConfig.sum_fields,
        page_size: updatedConfig.page_size,
        page: updatedConfig.page,
        metric: updatedConfig.metric,
        min_score: updatedConfig.min_score,
        include_vector: updatedConfig.include_vector,
        include_count: updatedConfig.include_count,
        hundred_scale: updatedConfig.hundred_scale,
      });
    }
    store.dispatch(RECOMMENDATION_CONFIG_ACTION(updatedConfig));
  }

  public resetRecommendationDocument() {
    const recommendationConfig = selectRecommendationConfig(store.getState());
    store.dispatch(
      RECOMMENDATION_CONFIG_ACTION({
        ...recommendationConfig,
        document_id: "",
        positive_document_ids: [],
        negative_document_ids: [],
        document_ids: [],
        setup: true,
      })
    );
    store.dispatch(SINGLE_RECOMMENDATION_ACTION.reset());
  }

  @TryCatchAction(SINGLE_RECOMMENDATION_ACTION)
  public async getMultiProductRecomendationLikeDislike(
    query: IMultiProductRecommendationLikeDislike
  ): Promise<ISingleProductRecommendation> {
    const collection = selectSelectedCollection(store.getState());
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    query.collection_name = collection.data?.name;
    const response:
      | ISingleProductRecommendationResponse
      | undefined = await this.get(
      "/collection/search_by_positive_negative_ids",
      query
    );

    if (!response) {
      throw new ApiError("Something went wrong...");
    }
    return {
      currentPage: query.page,
      pageSize: query.page_size,
      data: response.results,
      total: response.count,
      fields: getAllFields(collection.data?.schema || {}),
    };
  }
}

export default new RecommendationService();
